import { useQuery } from "@tanstack/react-query";
import Api from "src/Config";
import {
  createContext,
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";

import { textTruncate } from "src/helpers/truncateText";

export const GetMonitoringBulkProfilesContext = createContext(null);

type Props = {
  children: ReactElement;
};

const score_color: any = {
  LOW: "#05CD99",
  MEDIUM: "#F8961E",
  HIGH: "#F94144",
};

const score_bgcolor: any = {
  LOW: "#EBF9F1",
  MEDIUM: "#FEF2E5",
  HIGH: "#FBE7E8",
};

const GetMonitoringBulkProfilesProvider: FunctionComponent<Props> = ({
  children,
}) => {
  const { id } = useParams();
  const [queryParams, setQueryParams] = useSearchParams();
  const pageQuery = queryParams.get("page") || 1;

  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: [
      "bulk_profile_record",
      searchValue,
      pageQuery,
      queryParams.toString(),
    ],
    staleTime: 30000,
    queryFn: async () => {
      const url = searchValue
        ? `user_monitoring/bulks/${id}/profiles?search=${searchValue}&page_size=10&page=1`
        : `user_monitoring/bulks/${id}/profiles?page_size=10
        `;
      const { data } = await Api.get(`${url}`);

      const response = data.results.map(
        (res: {
          id: string;
          first_name: string;
          last_name: string;
          other_name: string;
          source: string;
          created_at: string;
          email: string;
          risk_status: string;
          risk_score: number;
          business_name: string;
          is_monitor: boolean;
        }) => ({
          id: res.id,
          name: `${
            !res.first_name?.toLowerCase() &&
            !res.last_name?.toLowerCase() &&
            !res.other_name?.toLowerCase() &&
            !res.business_name?.toLowerCase()
              ? "N/A"
              : res.business_name &&
                  !res.first_name?.toLowerCase() &&
                  !res.last_name?.toLowerCase() &&
                  !res.other_name?.toLowerCase()
                ? textTruncate(res.business_name, 20)
                : textTruncate(
                    `${res.first_name?.toLowerCase() || ""} ${
                      res.other_name?.toLowerCase() || ""
                    } ${res.last_name?.toLowerCase() || ""}`,
                    15,
                  )
          }`,
          source: `${res.source?.toLowerCase()} Check`,
          monitored: `${res.is_monitor ? "Yes" : "No"}`,
          date: format(new Date(res.created_at), "dd/MM/yyyy"),
          "email address": textTruncate(res.email?.toLowerCase(), 15) || "N/A",
          // row_disabled: res.is_monitor,
          status: (
            <div
              style={{
                color: score_color[res.risk_status],
                background: score_bgcolor[res.risk_status],
                width: "fit-content",
                textTransform: "lowercase",
                padding: "2px 15px",
                borderRadius: "23.29px",
              }}
            >
              {res.risk_status}
            </div>
          ),
          "risk score": (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div
                style={{
                  background: "#D9DDE3",
                  width: "100%",
                  borderRadius: "4px",
                }}
              >
                <div
                  style={{
                    background: score_color[res.risk_status],
                    height: "1.2vh",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    width: res.risk_score,
                  }}
                />
              </div>
              {res.risk_score.toFixed(2)}%
            </div>
          ),
        }),
      );
      return { response, metadata: data?.metadata };
    },
  });

  const handleSearch = (e: any) => {
    if (e.target.value) {
      setSearchParam(e.target.value);
    } else {
      setSearchValue("");
      setSearchParam("");
    }
  };

  const handleSearchRecord = () => {
    setSearchValue(searchParam);
  };

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  const contextValue = useMemo(
    () => ({
      data,
      isLoading,
      error,
      isFetching,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      setQueryParams,
      handleSearch,
      searchParam,
      handleSearchRecord,
    }),
    [
      data,
      isLoading,
      isFetching,
      error,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      setQueryParams,
      handleSearch,
      searchParam,
      handleSearchRecord,
    ],
  );
  return (
    <GetMonitoringBulkProfilesContext.Provider value={contextValue}>
      {children}
    </GetMonitoringBulkProfilesContext.Provider>
  );
};

export default GetMonitoringBulkProfilesProvider;
